(function () {
  const rootSelector = "[data-desktop-panel-scroll]";
  const panelSelector = ".product__info";
  const activeClass = "pdp-desktop-panel-scroll--active";
  const refreshEventName = "pdp-desktop-panel-scroll:refresh";
  const desktopQuery = window.matchMedia("(min-width: 992px)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const releaseProgressMultiplier = 2.25;
  const panelScrollEase = 0.24;
  const panelScrollSnapDistance = 0.75;
  const stickyCandidates = [
    "#shopify-section-announcement-bar",
    "#shopify-section-header",
    "#shopify-section-header-redesign",
    "#shopify-section-promo-bar",
    "#shopify-section-promo-bar-redesign"
  ];

  let frameId = 0;
  let panelAnimationFrameId = 0;
  const panelScrollTargets = new Map();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getViewportHeight = () => {
    if (window.visualViewport && window.visualViewport.height) {
      return window.visualViewport.height;
    }

    return window.innerHeight || document.documentElement.clientHeight || 0;
  };

  const getScrollingElement = () => document.scrollingElement || document.documentElement;

  const getPageScrollY = () => {
    const scrollingElement = getScrollingElement();

    return window.pageYOffset || scrollingElement.scrollTop || 0;
  };

  const getDocumentTop = (element) => element.getBoundingClientRect().top + getPageScrollY();

  const requestPanelAnimation = () => {
    if (panelAnimationFrameId) return;

    panelAnimationFrameId = window.requestAnimationFrame(animatePanelScroll);
  };

  const setPanelScroll = (panel, target, immediate) => {
    if (immediate || reducedMotionQuery.matches) {
      panelScrollTargets.delete(panel);
      panel.scrollTop = target;
      return;
    }

    panelScrollTargets.set(panel, target);
    requestPanelAnimation();
  };

  function animatePanelScroll() {
    panelAnimationFrameId = 0;
    let shouldContinue = false;

    panelScrollTargets.forEach((target, panel) => {
      if (!document.documentElement.contains(panel)) {
        panelScrollTargets.delete(panel);
        return;
      }

      const distance = target - panel.scrollTop;

      if (Math.abs(distance) <= panelScrollSnapDistance) {
        panel.scrollTop = target;
        panelScrollTargets.delete(panel);
        return;
      }

      panel.scrollTop += distance * panelScrollEase;
      shouldContinue = true;
    });

    if (shouldContinue) requestPanelAnimation();
  }

  const getStickyTop = () => {
    return stickyCandidates.reduce((offset, selector) => {
      const element = document.querySelector(selector);
      if (!element) return offset;

      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      const isVisible = rect.height > 0 && style.display !== "none" && style.visibility !== "hidden";
      const isSticky = style.position === "sticky" || style.position === "fixed";

      if (!isVisible || !isSticky) return offset;
      if (rect.top > offset + 2 || rect.bottom <= offset) return offset;

      return Math.max(offset, rect.bottom);
    }, 0);
  };

  const getItems = () => {
    return Array.from(document.querySelectorAll(rootSelector))
      .map((root) => ({
        root,
        section: root.closest("section[id^='MainProduct-']"),
        panel: root.querySelector(panelSelector)
      }))
      .filter((item) => item.section && item.panel);
  };

  const resetItem = (root) => {
    const section = root.closest("section[id^='MainProduct-']");
    const panel = root.querySelector(panelSelector);

    root.classList.remove(activeClass);
    root.classList.remove("pdp-desktop-panel-scroll--native");
    root.style.removeProperty("position");
    root.style.removeProperty("top");
    root.style.removeProperty("align-items");
    root.style.removeProperty("z-index");
    root.style.removeProperty("--pdp-scroll-sticky-top");
    root.style.removeProperty("--pdp-scroll-panel-height");
    delete root.dataset.panelScrollableDistance;
    delete root.dataset.panelStickyTop;
    delete root.dataset.panelRootOffset;

    if (section) {
      section.style.removeProperty("min-height");
      section.removeAttribute("data-panel-scroll-ready");
    }

    if (panel) {
      panelScrollTargets.delete(panel);
      panel.style.removeProperty("max-height");
      panel.style.removeProperty("overflow");
      panel.style.removeProperty("overscroll-behavior");
      panel.style.removeProperty("-webkit-overflow-scrolling");
      panel.scrollTop = 0;
    }
  };

  const measureItem = ({ root, section, panel }, stickyTop, rootOffset) => {
    const visualStickyTop = stickyTop + rootOffset;
    const panelHeight = Math.max(320, getViewportHeight() - visualStickyTop - 24);

    root.style.setProperty("--pdp-scroll-sticky-top", `${visualStickyTop}px`);
    root.style.setProperty("--pdp-scroll-panel-height", `${panelHeight}px`);

    const scrollableDistance = Math.max(0, panel.scrollHeight - panel.clientHeight);

    if (scrollableDistance <= 1) return 0;

    const stickyTrackDistance = scrollableDistance * releaseProgressMultiplier;
    const trackHeight = Math.ceil(root.offsetHeight + stickyTrackDistance);

    root.dataset.panelScrollableDistance = String(scrollableDistance);
    root.dataset.panelStickyTop = String(stickyTop);
    root.dataset.panelRootOffset = String(rootOffset);
    section.dataset.panelScrollReady = "true";
    section.style.minHeight = `${trackHeight}px`;
    panel.scrollTop = clamp(panel.scrollTop, 0, scrollableDistance);

    return scrollableDistance;
  };

  const updateScrollPosition = () => {
    frameId = 0;

    if (!desktopQuery.matches) return;

    getItems().forEach(({ root, section, panel }) => {
      if (!root.classList.contains(activeClass)) return;

      let scrollableDistance = Number(root.dataset.panelScrollableDistance || 0);
      if (scrollableDistance <= 0) return;

      const stickyTop = Math.ceil(getStickyTop());
      const storedStickyTop = Number(root.dataset.panelStickyTop || 0);

      if (Math.abs(stickyTop - storedStickyTop) > 1) {
        const rootOffset = Number(root.dataset.panelRootOffset || 0);
        scrollableDistance = measureItem({ root, section, panel }, stickyTop, rootOffset);
      }

      const rootOffset = Number(root.dataset.panelRootOffset || 0);
      const start = getDocumentTop(section) - stickyTop;
      const progress = getPageScrollY() - start;
      const stickyTrackDistance = scrollableDistance * releaseProgressMultiplier;
      const targetScroll = clamp(progress / releaseProgressMultiplier, 0, scrollableDistance);
      const shouldSnap = progress <= 0 || progress >= stickyTrackDistance;

      setPanelScroll(panel, targetScroll, shouldSnap);
    });
  };

  const requestScrollUpdate = () => {
    if (frameId) return;

    frameId = window.requestAnimationFrame(updateScrollPosition);
  };

  const refreshItem = (item) => {
    const { root } = item;

    if (!desktopQuery.matches) {
      resetItem(root);
      return;
    }

    const stickyTop = Math.ceil(getStickyTop());
    const storedRootOffset = Number(root.dataset.panelRootOffset);
    const rootOffset = Number.isFinite(storedRootOffset)
      ? storedRootOffset
      : Math.max(0, getDocumentTop(root) - getDocumentTop(item.section));

    root.classList.add(activeClass);
    const scrollableDistance = measureItem(item, stickyTop, rootOffset);

    if (scrollableDistance <= 1) {
      resetItem(root);
      return;
    }

    updateScrollPosition();
  };

  const refresh = () => {
    getItems().forEach(refreshItem);
    requestScrollUpdate();
  };

  const scheduleRefresh = () => {
    window.setTimeout(refresh, 50);
    window.setTimeout(refresh, 400);
  };

  const init = () => {
    refresh();
    window.setTimeout(refresh, 250);
    window.setTimeout(refresh, 1000);
    window.addEventListener("scroll", requestScrollUpdate, { passive: true });
    window.addEventListener("resize", refresh);
    window.addEventListener("load", refresh);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", refresh);
    }

    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(rootSelector)) return;
      if (event.target.closest(".product-faq__question")) return;

      scheduleRefresh();
    });

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", refresh);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(refresh);
    }

    document.addEventListener("shopify:section:load", refresh);
    document.addEventListener(refreshEventName, scheduleRefresh);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
